/*
 * TAKEDA CONFIDENTIAL – Highly Restricted: Do not distribute without prior approval
 *
 * © Copyright (2023) Takeda. All Rights Reserved
 */

import { useContext } from 'react';
import { useCookies } from 'react-cookie';

import { getCookieOptions } from '../../../../domain/cookies/cookies.domain';
import {
  getCountryFromLocale,
  getLanguageFromLocale,
} from '../../../../domain/global-config.domain';
import { LOCALE_COOKIE } from '../../../authentication/authentication.constants';
import { LocaleContext } from '../../contexts/locale/locale.context';

export const useLocale = () => {
  const [cookies, setCookie, removeCookie] = useCookies([LOCALE_COOKIE]);
  const context = useContext(LocaleContext);

  if (!context) {
    throw new Error(
      'useLocale must be used inside a LocaleContextProvider'
    );
  }

  const locale = cookies[LOCALE_COOKIE] || context.locale;

  const country = getCountryFromLocale(locale) || '';

  const language = getLanguageFromLocale(locale) || '';

  const setLocale = (locale: string) => {
    setCookie(LOCALE_COOKIE, locale, getCookieOptions());

    // writing to the context so the values are available in an iframe
    context.setLocale(locale);
  };

  const clearLocale = () => {
    removeCookie(LOCALE_COOKIE, getCookieOptions());
    context.setLocale(undefined);
  };

  return {
    country,
    language,
    locale,
    clearLocale,
    setLocale,
    context,
  };
};
